/**
 * Task Import Vuex Mutations
 * State modifications for the task import feature.
 */

import { createInitialState } from './state';

export default {
  // ==========================================================================
  // Wizard State
  // ==========================================================================

  SET_WIZARD_OPEN(state, open) {
    state.wizardOpen = open;
  },

  SET_WIZARD_STEP(state, step) {
    state.wizardStep = step;
  },

  // ==========================================================================
  // Import Mode
  // ==========================================================================

  SET_IMPORT_MODE(state, mode) {
    state.importMode = mode;
  },

  // ==========================================================================
  // Scope Selection (mutually exclusive)
  // ==========================================================================

  SET_SCOPE_TYPE(state, type) {
    state.scopeType = type;
    // Clear the non-selected scope ID (ForgeBaseTag pattern)
    if (type === 'Facility') {
      state.selectedFacilityGroupId = null;
    } else {
      state.selectedFacilityId = null;
    }
  },

  SET_SELECTED_FACILITY_ID(state, id) {
    state.selectedFacilityId = id;
    // Ensure mutual exclusivity
    if (id) {
      state.selectedFacilityGroupId = null;
      state.scopeType = 'Facility';
    }
  },

  SET_SELECTED_FACILITY_GROUP_ID(state, id) {
    state.selectedFacilityGroupId = id;
    // Ensure mutual exclusivity
    if (id) {
      state.selectedFacilityId = null;
      state.scopeType = 'FacilityGroup';
    }
  },

  // ==========================================================================
  // TaskBoard Selection
  // ==========================================================================

  SET_SELECTED_TASK_TYPE_ID(state, id) {
    state.selectedTaskTypeId = id;
  },

  SET_SELECTED_TASK_TYPE(state, taskType) {
    state.selectedTaskType = taskType;
    if (taskType) {
      state.selectedTaskTypeId = taskType.id;
    }
  },

  SET_BOARD_SELECTION_DISABLED(state, disabled) {
    state.boardSelectionDisabled = disabled;
  },

  SET_AVAILABLE_TASK_TYPES(state, taskTypes) {
    state.availableTaskTypes = taskTypes;
  },

  SET_TASK_TYPES_LOADING(state, loading) {
    state.taskTypesLoading = loading;
  },

  // ==========================================================================
  // New TaskBoard
  // ==========================================================================

  SET_NEW_BOARD_NAME(state, name) {
    state.newBoardName = name;
  },

  SET_NEW_BOARD_DESCRIPTION(state, description) {
    state.newBoardDescription = description;
  },

  // ==========================================================================
  // Data Source Type
  // ==========================================================================

  SET_DATA_SOURCE_TYPE(state, type) {
    state.dataSourceType = type;
  },

  // ==========================================================================
  // Inline API Configuration
  // ==========================================================================

  SET_API_CONFIG(state, config) {
    state.apiConfig = config;
  },

  UPDATE_API_CONFIG(state, partial) {
    state.apiConfig = { ...state.apiConfig, ...partial };
  },

  SET_API_TEST_RESULT(state, result) {
    state.apiTestResult = result;
  },

  SET_API_PREVIEW_DATA(state, data) {
    state.apiPreviewData = data;
  },

  // ==========================================================================
  // File Upload / Data
  // ==========================================================================

  SET_PARSED_DATA(state, data) {
    state.parsedData = data;
  },

  SET_EXCEL_COLUMNS(state, columns) {
    state.excelColumns = columns;
  },

  SET_SOURCE_FILE_NAME(state, name) {
    state.sourceFileName = name;
  },

  // ==========================================================================
  // Field Mappings
  // ==========================================================================

  SET_SYSTEM_FIELD_MAPPINGS(state, mappings) {
    state.systemFieldMappings = mappings;
  },

  SET_SYSTEM_FIELD_MAPPING(state, { fieldKey, excelColumn }) {
    state.systemFieldMappings = {
      ...state.systemFieldMappings,
      [fieldKey]: excelColumn,
    };
  },

  CLEAR_SYSTEM_FIELD_MAPPING(state, fieldKey) {
    const { [fieldKey]: removed, ...rest } = state.systemFieldMappings;
    state.systemFieldMappings = rest;
  },

  SET_CUSTOM_FIELD_MAPPINGS(state, mappings) {
    state.customFieldMappings = mappings;
  },

  SET_CUSTOM_FIELD_MAPPING(state, { fieldId, excelColumn }) {
    state.customFieldMappings = {
      ...state.customFieldMappings,
      [fieldId]: excelColumn,
    };
  },

  CLEAR_CUSTOM_FIELD_MAPPING(state, fieldId) {
    const { [fieldId]: removed, ...rest } = state.customFieldMappings;
    state.customFieldMappings = rest;
  },

  // ==========================================================================
  // New Custom Fields (for new TaskBoard mode)
  // ==========================================================================

  SET_NEW_CUSTOM_FIELDS(state, fields) {
    state.newCustomFields = fields;
  },

  ADD_NEW_CUSTOM_FIELD(state, field) {
    state.newCustomFields.push(field);
  },

  UPDATE_NEW_CUSTOM_FIELD(state, { index, field }) {
    if (index >= 0 && index < state.newCustomFields.length) {
      state.newCustomFields.splice(index, 1, field);
    }
  },

  REMOVE_NEW_CUSTOM_FIELD(state, index) {
    if (index >= 0 && index < state.newCustomFields.length) {
      state.newCustomFields.splice(index, 1);
    }
  },

  // ==========================================================================
  // Sections (for new TaskBoard organization)
  // ==========================================================================

  SET_SECTIONS(state, sections) {
    state.sections = sections;
  },

  // ==========================================================================
  // TaskBoard Sections Configuration (for Configure TaskBoard dialog)
  // ==========================================================================

  SET_TASKBOARD_SECTIONS(state, sections) {
    state.taskBoardSections = sections;
  },

  ADD_TASKBOARD_SECTION(state, section) {
    state.taskBoardSections.push(section);
  },

  UPDATE_TASKBOARD_SECTION(state, { index, section }) {
    if (index >= 0 && index < state.taskBoardSections.length) {
      state.taskBoardSections.splice(index, 1, section);
    }
  },

  REMOVE_TASKBOARD_SECTION(state, index) {
    if (index >= 0 && index < state.taskBoardSections.length) {
      state.taskBoardSections.splice(index, 1);
    }
  },

  // ==========================================================================
  // Key Column & Duplicate Handling
  // ==========================================================================

  SET_KEY_COLUMN(state, column) {
    state.keyColumn = column;
  },

  SET_DUPLICATE_CHECK_RESULT(state, result) {
    state.duplicateCheckResult = result;
  },

  SET_DUPLICATE_HANDLING(state, handling) {
    state.duplicateHandling = handling;
  },

  // ==========================================================================
  // User Email Resolution (for Users field type)
  // ==========================================================================

  SET_USER_EMAIL_MAPPINGS(state, mappings) {
    state.userEmailMappings = mappings;
  },

  SET_UNRESOLVED_USER_EMAILS(state, emails) {
    state.unresolvedUserEmails = emails;
  },

  SET_RESOLVING_USERS(state, resolving) {
    state.resolvingUsers = resolving;
  },

  CLEAR_USER_RESOLUTION(state) {
    state.userEmailMappings = {};
    state.unresolvedUserEmails = [];
    state.resolvingUsers = false;
  },

  // ==========================================================================
  // Loading States
  // ==========================================================================

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_IMPORTING(state, importing) {
    state.importing = importing;
  },

  SET_CHECKING_DUPLICATES(state, checking) {
    state.checkingDuplicates = checking;
  },

  SET_EXPORTING(state, exporting) {
    state.exporting = exporting;
  },

  SET_HISTORY_LOADING(state, loading) {
    state.historyLoading = loading;
  },

  // ==========================================================================
  // Error Handling
  // ==========================================================================

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  // ==========================================================================
  // Import Result
  // ==========================================================================

  SET_IMPORT_RESULT(state, result) {
    state.importResult = result;
  },

  // ==========================================================================
  // Import History Sidebar
  // ==========================================================================

  SET_HISTORY_SIDEBAR_OPEN(state, open) {
    state.historySidebarOpen = open;
  },

  SET_IMPORT_HISTORY(state, history) {
    state.importHistory = history;
  },

  APPEND_IMPORT_HISTORY(state, items) {
    state.importHistory = [...state.importHistory, ...items];
  },

  SET_IMPORT_HISTORY_TOTAL(state, total) {
    state.importHistoryTotal = total;
  },

  SET_HISTORY_FILTERS(state, filters) {
    state.historyFilters = { ...state.historyFilters, ...filters };
  },

  RESET_HISTORY_FILTERS(state) {
    state.historyFilters = {
      page: 1,
      itemsPerPage: 20,
      status: null,
      fromDate: null,
      toDate: null,
    };
  },

  CLEAR_IMPORT_HISTORY(state) {
    state.importHistory = [];
    state.importHistoryTotal = 0;
  },

  // ==========================================================================
  // Export
  // ==========================================================================

  SET_EXPORT_RESULT(state, result) {
    state.exportResult = result;
  },

  // ==========================================================================
  // API Data Sources
  // ==========================================================================

  SET_API_DATA_SOURCES(state, dataSources) {
    state.apiDataSources = dataSources;
  },

  SET_API_DATA_SOURCES_TOTAL(state, total) {
    state.apiDataSourcesTotal = total;
  },

  SET_API_DATA_SOURCE_LOADING(state, loading) {
    state.apiDataSourceLoading = loading;
  },

  ADD_API_DATA_SOURCE(state, dataSource) {
    state.apiDataSources.push(dataSource);
  },

  UPDATE_API_DATA_SOURCE(state, updated) {
    const index = state.apiDataSources.findIndex(ds => ds.id === updated.id);
    if (index !== -1) {
      state.apiDataSources.splice(index, 1, updated);
    }
  },

  REMOVE_API_DATA_SOURCE(state, id) {
    state.apiDataSources = state.apiDataSources.filter(ds => ds.id !== id);
  },

  // ==========================================================================
  // API Execution History
  // ==========================================================================

  SET_API_EXECUTION_HISTORY(state, history) {
    state.apiExecutionHistory = history;
  },

  SET_API_EXECUTION_HISTORY_TOTAL(state, total) {
    state.apiExecutionHistoryTotal = total;
  },

  SET_API_EXECUTION_HISTORY_LOADING(state, loading) {
    state.apiExecutionHistoryLoading = loading;
  },

  // ==========================================================================
  // Reset States
  // ==========================================================================

  /**
   * Reset wizard state to initial values.
   * Called when opening/closing wizard.
   */
  RESET_WIZARD_STATE(state) {
    const initial = createInitialState();

    // Reset wizard-related state
    state.wizardStep = initial.wizardStep;
    state.importMode = initial.importMode;
    state.dataSourceType = initial.dataSourceType;
    state.scopeType = initial.scopeType;
    state.selectedFacilityId = initial.selectedFacilityId;
    state.selectedFacilityGroupId = initial.selectedFacilityGroupId;
    state.selectedTaskTypeId = initial.selectedTaskTypeId;
    state.selectedTaskType = initial.selectedTaskType;
    state.boardSelectionDisabled = initial.boardSelectionDisabled;
    state.newBoardName = initial.newBoardName;
    state.newBoardDescription = initial.newBoardDescription;
    state.apiConfig = initial.apiConfig;
    state.apiTestResult = initial.apiTestResult;
    state.apiPreviewData = initial.apiPreviewData;
    state.parsedData = initial.parsedData;
    state.excelColumns = initial.excelColumns;
    state.sourceFileName = initial.sourceFileName;
    state.systemFieldMappings = initial.systemFieldMappings;
    state.customFieldMappings = initial.customFieldMappings;
    state.newCustomFields = initial.newCustomFields;
    state.sections = initial.sections;
    state.taskBoardSections = initial.taskBoardSections;
    state.keyColumn = initial.keyColumn;
    state.duplicateCheckResult = initial.duplicateCheckResult;
    state.duplicateHandling = initial.duplicateHandling;
    state.userEmailMappings = initial.userEmailMappings;
    state.unresolvedUserEmails = initial.unresolvedUserEmails;
    state.resolvingUsers = initial.resolvingUsers;
    state.importResult = initial.importResult;
    state.error = initial.error;
    // Keep availableTaskTypes cached
  },

  /**
   * Reset all state to initial values.
   */
  RESET_ALL(state) {
    Object.assign(state, createInitialState());
  },
};
